import { DateTime, Duration, Info } from 'luxon';
import { IGradeDisciplinas } from '../../components/grade_horaria';
import { IColunaGradeHoraria, ILinhaGradeHoraria } from '../../components/calendario_horarios';

import {
  GradePageQuery_alocacoes_professores,
  GradePageQuery_feriados,
  GradePageQuery_projetos_pedagogicos,
  GradePageQuery_turma_horario,
} from '../../graphql/queries/__generated__/GradePageQuery';

import { PeriodosQuery_periodos } from '../../graphql/queries/__generated__/PeriodosQuery';
import { mapear_feriados } from '../maps/feriados.map';
import { gerar_horarios } from './horarios.generator';

interface IGradeHoraria {
  nome_mes: string;
  colunas: IColunaGradeHoraria[];
  linhas: ILinhaGradeHoraria[];
}

interface Props {
  feriados: Array<GradePageQuery_feriados>;
  periodo: PeriodosQuery_periodos;
  horario: GradePageQuery_turma_horario;
  alocacoes_professor: Array<GradePageQuery_alocacoes_professores>;
  projetos_pedagogico: Array<GradePageQuery_projetos_pedagogicos>;
}

const HORA_AULA = 50;
const INTERVALO = 10;

/**
 * Gera grade horária que será exibida na tela
 * */
const gerar_grade_horaria = async (props: Props) => {
  const map_professores_disciplinas = new Map();
  const map_alocacoes = new Map();
  const map_datas_alocacao_disciplina = new Map<string, { primeira: typeof DateTime.prototype; ultima: typeof DateTime.prototype }>();
  const map_feriados = mapear_feriados(props.feriados);
  const dias_alocacao = [0, 0, 0, 0, 0, 0, 0];
  const disciplinas: Array<IGradeDisciplinas> = [];
  const grade_horaria: IGradeHoraria[] = [];

  props.alocacoes_professor.forEach((a) => {
    if (a.ind_status != 4) {
      const dat_alocacao = DateTime.fromISO(a.dat_alocacao, { setZone: true });

      map_alocacoes.set(dat_alocacao.toISODate(), a);
      dias_alocacao[dat_alocacao.weekday - 1]++;

      const hora_inicio = DateTime.fromISO(a.horario.hora_inicio);
      const hora_fim = DateTime.fromISO(a.horario.hora_fim);

      const diferenca = Duration.fromObject({
        hours: hora_fim.hour - hora_inicio.hour,
        minutes: hora_fim.minute - hora_inicio.minute,
      });

      const total_minutos = (diferenca.hours * 60) + diferenca.minutes;
      const total_minutos_descontado = total_minutos > HORA_AULA * 2 ? total_minutos - INTERVALO : total_minutos;
      const qtde_horas = total_minutos_descontado / HORA_AULA;
      const professorDisciplina = map_professores_disciplinas.get(a.disciplina.dsc_sigla);

      if (professorDisciplina) {
        professorDisciplina.qtde_horas += qtde_horas;
      } else {
        map_professores_disciplinas.set(a.disciplina.dsc_sigla, {
          nome_professor: a.professor.dsc_nome_professor,
          qtde_horas: qtde_horas,
        });
      }

      // Rastrear primeira e última alocação por disciplina
      const dsc_sigla_alocacao = a.disciplina.dsc_sigla || '';
      const datesEntry = map_datas_alocacao_disciplina.get(dsc_sigla_alocacao);
      if (datesEntry) {
        if (dat_alocacao < datesEntry.primeira) datesEntry.primeira = dat_alocacao;
        if (dat_alocacao > datesEntry.ultima) datesEntry.ultima = dat_alocacao;
      } else {
        map_datas_alocacao_disciplina.set(dsc_sigla_alocacao, {
          primeira: dat_alocacao,
          ultima: dat_alocacao,
        });
      }
    }
  });

  props.projetos_pedagogico.forEach((p) => {
    const id_disciplina = p.disciplina.id_disciplina;
    const dsc_nome_disciplina = p.disciplina.dsc_nome_disciplina;
    const dsc_sigla_base = p.disciplina.dsc_sigla || '';
    const dsc_sigla_display = p.dsc_sigla_disciplina || p.disciplina.dsc_sigla || '';
    const num_carga_horaria = p.disciplina.num_carga_horaria || 1;
    const ind_semestre = p.ind_semestre || 0;
    let dsc_nome_professor = p.professor?.dsc_nome_professor || '';
    let qtd_horas_alocadas = 0;

    const professorDisciplina = map_professores_disciplinas.get(dsc_sigla_base);

    if (professorDisciplina) {
      dsc_nome_professor = professorDisciplina.nome_professor;
      qtd_horas_alocadas = professorDisciplina.qtde_horas;
    }

    // Definir datas de início e fim baseado na modalidade
    let dat_inicio: string | undefined;
    let dat_fim: string | undefined;

    if (p.ind_modalidade === 2) {
      // EAD: usar datas do projeto pedagógico
      dat_inicio = p.dat_inicio;
      dat_fim = p.dat_fim;
    } else {
      // Presencial/Outros: usar primeira e última alocação
      const datasAlocacao = map_datas_alocacao_disciplina.get(dsc_sigla_base);
      if (datasAlocacao) {
        dat_inicio = datasAlocacao.primeira.toISO();
        dat_fim = datasAlocacao.ultima.toISO();
      }
    }

    const disciplina = {
      ind_semestre,
      id_disciplina,
      dsc_nome_disciplina: dsc_nome_disciplina || '',
      dsc_sigla: dsc_sigla_display,
      num_carga_horaria: num_carga_horaria,
      num_carga_horaria_to: p.num_carga_horaria_to || null,
      dat_inicio,
      dat_fim,
      progesso_carga_horaria: Math.round((100 * qtd_horas_alocadas) / num_carga_horaria) + '%',
      dsc_nome_professor,
      dsc_modalidade: p.dsc_modalidade || '',
    };

    disciplinas.push(disciplina);
  });

  const dias_do_curso: Array<number> = [];

  for (let i = 0; i < 6; i++) {
    if (dias_alocacao[i] > 3) {
      dias_do_curso.push(i);
    }
  }

  let data_inicial = DateTime.fromISO(props.periodo.dat_inicio, { setZone: true });
  const data_final = DateTime.fromISO(props.periodo.dat_fim, { setZone: true });

  while (data_inicial <= data_final) {
    const mes = data_inicial.month - 1;
    const dia_curso = dias_do_curso.includes(data_inicial.weekday - 1);
    const chave = data_inicial.toISODate();
    const alocacao = map_alocacoes.get(chave);
    const feriado = Boolean(map_feriados.get(chave));
    const disciplina = !feriado ? alocacao?.disciplina?.dsc_sigla || '' : 'FERIADO';

    if (alocacao || dia_curso) {
      const nova_coluna = {
        nome: Info.weekdays('short', { locale: 'pt-BR' })[data_inicial.weekday - 1],
        numero: data_inicial.day,
      };

      const grade_horaria_mes = grade_horaria[mes];
      const horarios = gerar_horarios(props.horario);

      if (grade_horaria_mes) {
        horarios.forEach((horario, index) => {
          grade_horaria_mes.linhas[index][data_inicial.day] = disciplina;
          grade_horaria_mes.linhas[index].horario = horario.hora_inicio + ' - ' + horario.hora_fim;
        });

        grade_horaria_mes.colunas.push(nova_coluna);
      } else {
        const nova_linha = {} as ILinhaGradeHoraria;
        nova_linha[data_inicial.day] = disciplina;

        grade_horaria[mes] = {
          nome_mes: Info.months('long', { locale: 'pt-BR' })[mes],
          linhas: horarios.map((horario) => ({
            ...nova_linha,
            horario: horario.hora_inicio + ' - ' + horario.hora_fim,
          })),
          colunas: [nova_coluna],
        };
      }
    }

    data_inicial = data_inicial.plus({ days: 1 });
  }

  return { disciplinas, grade_horaria };
};

export { gerar_grade_horaria };
export type { IGradeHoraria };
